import type { IntakeData } from './types';
import { getSpecialtyTag } from './specialtyTags';

const getBackendUrl = () =>
  import.meta.env.VITE_JEMA_BACKEND_URL || "http://localhost:3000";

const handleApiResponse = async (response: Response) => {
  if (!response.ok) {
    let errorMessage = "An error occurred";
    let errorDetails: any = null;
    try {
      const errorData = await response.json();
      errorDetails = errorData;
      
      // Log full error for debugging
      console.error("API Error Response:", {
        status: response.status,
        statusText: response.statusText,
        errorData,
      });
      
      if (errorData.error) {
        if (typeof errorData.error === "string") {
          errorMessage = errorData.error;
        } else if (errorData.error.message) {
          errorMessage = errorData.error.message;
          // Include validation details if available
          if (errorData.error.details) {
            errorMessage += `: ${JSON.stringify(errorData.error.details)}`;
          } else if (errorData.error.validationErrors) {
            errorMessage += `: ${JSON.stringify(errorData.error.validationErrors)}`;
          }
        } else if (errorData.error.code) {
          errorMessage = errorData.error.code;
        } else {
          errorMessage = JSON.stringify(errorData.error);
        }
      } else if (errorData.message) {
        errorMessage = errorData.message;
        // Include validation details if available
        if (errorData.details) {
          errorMessage += `: ${JSON.stringify(errorData.details)}`;
        } else if (errorData.validationErrors) {
          errorMessage += `: ${JSON.stringify(errorData.validationErrors)}`;
        }
      } else {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
    } catch (parseError) {
      // If JSON parsing fails, try to get text
      try {
        const text = await response.text();
        console.error("API Error (non-JSON):", text);
        errorMessage = text || `HTTP ${response.status}: ${response.statusText}`;
      } catch {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
    }
    
    const error = new Error(errorMessage);
    (error as any).details = errorDetails;
    (error as any).status = response.status;
    throw error;
  }

  return response.json();
};

type StartIntakeApiResponse = {
  data: {
    formUid: string;
    referenceUid: string;
    specialty: string;
    status: string;
    patientCreated: boolean;
    requiresVerification: boolean;
    patientId?: string;
  };
};

type MagicLinkChallenge = {
  method: string;
  challengeId?: string;
  expiresAt?: string;
  requiresOtpFallback: boolean;
  alreadySent?: boolean;
  sentAt?: string;
};

type OtpChallenge = {
  challengeId: string;
  method: string;
  expiresAt: string;
  message: string;
};

type FinalizeIntakeApiResponse = {
  data: {
    intakeForm: Record<string, unknown>;
    serviceRequest?: Record<string, unknown>;
    magicLink?: MagicLinkChallenge;
    otpChallenge?: OtpChallenge;
  };
};

export type MediaUploadResponse = {
  data: {
    uploadUrl: string;
    bucketName: string;
    storagePath: string;
    expiresIn: number;
  };
};

export type MediaRegistrationResponse = {
  data?: {
    media?: {
      id: string;
      mediaType: string;
      status?: string;
      downloadUrl?: string;
      downloadUrlExpiresIn?: number;
      photoPosition?: string;
      photoSequence?: number;
    };
  };
};

type BatchPresignUploadRequest = {
  clientReference: string;
  mimeType: string;
  size: number;
  originalFilename?: string;
};

type BatchPresignUploadResponse = {
  data: {
    uploads: Array<{
      clientReference: string;
      uploadUrl: string;
      bucketName: string;
      storagePath: string;
      expiresIn: number;
    }>;
  };
};

type RegisteredMedia = NonNullable<MediaRegistrationResponse["data"]>["media"];

type BatchMediaRegistrationResponse = {
  data: {
    items: Array<{
      clientReference: string;
      media: RegisteredMedia;
    }>;
  };
};

export type Specialty = {
  id: string;
  slug: string;
  name: string;
  category: string;
  description: string | null;
  display_order: number;
};

type BatchMediaRegistrationRequestItem = {
  clientReference: string;
  mediaType: "photo";
  s3BucketName: string;
  s3StoragePath: string;
  originalFilename: string;
  mimeType: string;
  fileSize: number;
  photoPosition?: string;
  photoSequence?: number;
  replaceMediaId?: string;
  metadata?: Record<string, unknown>;
};

type VerificationResponse = {
  data: {
    success: boolean;
    accessToken?: string;
    refreshToken?: string;
    expiresAt?: number;
    user: {
      id: string;
      email: string;
      role: string;
      onboardingComplete: boolean;
    };
    intakeForm?: Record<string, unknown>;
    serviceRequest?: Record<string, unknown>;
  };
};

const MEDICAL_QUESTION_ORDER = [
  "dateOfBirth",
  "height",
  "weight",
  "currentMedicalConditions",
  "preExistingConditions",
  "otherCondition",
  "pastSurgeries",
  "currentMedications",
  "allergies",
  "allergiesMedications",
  "smokingHabits",
  "alcoholUse",
  "alcoholConsumption",
  "exerciseFrequency",

  //NEED TO REMOVE
  "travelConcerns",
  "budgetExpectations",
  "personalInterest",
  "travelAlone",

  //DENTAL ONLY
  "dentalProcedureType",
  "dentalDiscomfort",
  "mobilityIssues",
  "dietaryRestrictions",

] as const satisfies readonly (keyof IntakeData)[];

// Optional experience keys for tours and services
const OPTIONAL_EXPERIENCE_KEYS = [
  "selectedServices",
  "selectedTours",
  "selectedExperience",
  "additionalServicesCost",
] as const satisfies readonly (keyof IntakeData)[];


// Optional experience keys for tours and services
const DENTISTRY_DYNAMIC_QUESTION_KEYS = [
  "orthodonticHistory",
  "orthodonticGoals",
  "treatmentDuration",
  "appliancePreference",
  "missingTeethCount",
  "missingTeethDuration",
  "boneGraftHistory",
  "implantExpectations",
  "cosmeticGoals",
  "previousCosmetic",
  "smileConfidence",
  "socialImpact",
  "reconstructionReason",
  "currentPainLevel",
  "functionIssues",
  "treatmentPriority",
  "veneerGoals",
  "teethToTreat",
  "currentTeethCondition",
  "previousCosmetic",
  "crownReason",
  "numberOfCrowns",
  "crownMaterial",
  "urgencyLevel",
  "whiteningGoal",
  "stainingType",
  "previousWhitening",
  "sensitivityConcern",
  "lastDentalVisit",
  "dentalConcerns",
  "treatmentInterest",
 
];

const normalizeAnswer = (
  value: unknown,
): string | number | boolean | string[] | Record<string, unknown> | null => {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? (value as string[]) : null;
  }

  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    return entries.length > 0 ? (value as Record<string, unknown>) : null;
  }

  return null;
};

// Humanize camelCase/underscore keys for easier downstream reading
const formatQuestionLabel = (questionId: string): string => {
  const withSpaces = questionId
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2");

  return withSpaces
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

type IntakeQuestionPayload = {
  id: string;
  prompt: string;
  type?: string;
  category?: string;
  options?: string[];
  metadata?: Record<string, unknown>;
};

//PAYLOAD BUILD
export const buildFormPayload = (data: IntakeData) => {
  // Clean and validate identity fields - ensure required fields are not empty
  const cleanString = (value: string | undefined): string | undefined => {
    if (!value || typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  };

  // Build identity object
  // Required fields are always included (even if empty) so backend can validate
  // Optional fields are only included if they have values
  const identity: Record<string, string> = {
    firstName: cleanString(data.firstName) || '',
    lastName: cleanString(data.lastName) || '',
    email: cleanString(data.email) || '',
  };
  
  // Optional fields - only include if present
  const phone = cleanString(data.phone);
  if (phone) identity.phone = phone;
  
  const dateOfBirth = cleanString(data.dateOfBirth);
  if (dateOfBirth) identity.dateOfBirth = dateOfBirth;
  
  const city = cleanString(data.city);
  if (city) identity.city = city;
  
  const state = cleanString(data.state);
  if (state) identity.state = state;
  
  const country = cleanString(data.country);
  if (country) identity.country = country;
  
  if (data.preferredLanguage) {
    identity.preferredLanguage = data.preferredLanguage;
  }

  const responses: Array<{
    questionId: string;
    answer: string | number | boolean | string[] | Record<string, unknown> | null;
    question?: IntakeQuestionPayload;
  }> = [];

  const pushResponse = (
    questionId: keyof IntakeData,
    includeWhenNull = true,
  ) => {
    const answer = normalizeAnswer((data as IntakeData)[questionId]);
    if (answer === null && !includeWhenNull) {
      return;
    }
    responses.push({
      questionId,
      question: {
        id: String(questionId),
        prompt: formatQuestionLabel(questionId),
      },
      answer,
    });
  };

  // Push medical questions in order
  MEDICAL_QUESTION_ORDER.forEach((questionId) => {
    pushResponse(questionId as keyof IntakeData);
  });

  // Push optional experience keys
  OPTIONAL_EXPERIENCE_KEYS.forEach((questionId) => {
    pushResponse(questionId as keyof IntakeData, false);
  });
  
  DENTISTRY_DYNAMIC_QUESTION_KEYS.forEach((questionId) => {
    pushResponse(questionId as keyof IntakeData, false);
  });

  // Determine specialty from selected procedure using slug-to-enum mapping
  // The procedures field can be:
  // 1. A ProcedureType object (from database) with slug property
  // 2. A string slug (legacy support)
  // 3. Empty/null (defaults to dentistry)
  const procedures: any = data.procedures;
  let specialty = "dentistry";
  
  if (procedures) { 
    if (typeof procedures === 'string') {
      // Handle string slug
      specialty = getSpecialtyTag(procedures);
    } else if (typeof procedures === 'object') {
      // Handle ProcedureType object from database
      if (procedures.slug) {
        specialty = getSpecialtyTag(procedures);
      } else if (procedures.category) {
        // Legacy fallback for category-based mapping
        specialty = getSpecialtyTag(procedures.category);
      }
    }
  }

  //TODO
  // Object.keys(data).forEach((key) => {
  //   if (
  //     !responses.find(r => r.questionId === key) &&
  //     !["firstName","lastName","email","phone",
  //       "dateOfBirth","city","state","country",
  //       "preferredLanguage","procedures", 
  //       "upper_occlusal_timestamp",
  //        "frontal_retracted_apart_timestamp",
  //         "lower_occlusal_timestamp", "frontal_in_occlusion_timestamp", 
  //         "right_buccal_timestamp", "left_buccal_timestamp","heightFeet","heightInches" 
  //         ,"formUid" , "referenceUid","emailVerified","consentGiven" , "photos" , "uploads"].includes(key)
  //   ) { 
  //     responses.push({ 
  //       questionId: key,
  //       answer: normalizeAnswer((data as any)[key])
  //     });
  //   }
  // });  
  //TODO

  return {
    identity,
    specialty,
    responses,
    portalContext: "patient" as const,
  };
};

export const patientIntakeApi = {
  
  async submitIntake(data: IntakeData) {
    const payload = buildFormPayload(data);
    
    // Log payload for debugging validation errors
    console.log("Submitting intake with payload:", {
      identity: payload.identity,
      specialty: payload.specialty,
      responseCount: payload.responses.length,
      sampleResponses: payload.responses.slice(0, 5),
    });
    
    const startResponse = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ formData: payload }),
      },
    );
    const startResult = (await handleApiResponse(
      startResponse,
    )) as StartIntakeApiResponse;
    return { data: startResult.data };
  },

  async finalizeIntake(formUid: string) {
    const finalizeResponse = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms/${formUid}/finalize`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ submittedAt: new Date().toISOString() }),
      },
    );
    return handleApiResponse(
      finalizeResponse,
    ) as Promise<FinalizeIntakeApiResponse>;
  },

  async getPhotoUploadUrl(
    formUid: string,
    mimeType: string,
    size: number,
    originalFilename: string,
  ): Promise<MediaUploadResponse> {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms/${formUid}/media/presign`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mimeType, size, originalFilename }),
      },
    );

    return handleApiResponse(response);
  },

  async uploadPhotoToCloud(uploadUrl: string, file: File) {
    const response = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type },
    });

    if (!response.ok) {
      throw new Error("Failed to upload photo to cloud storage");
    }
  },

  async registerPhoto(
    formUid: string,
    bucketName: string,
    storagePath: string,
    originalFilename: string,
    mimeType: string,
    fileSize: number,
    photoPosition?: string,
    photoSequence?: number,
    replaceMediaId?: string,
  ): Promise<MediaRegistrationResponse> {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms/${formUid}/media`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mediaType: "photo",
          s3BucketName: bucketName,
          s3StoragePath: storagePath,
          originalFilename,
          mimeType,
          fileSize,
          photoPosition,
          photoSequence,
          replaceMediaId,
        }),
      },
    );

    return handleApiResponse(response);
  },

  async getBatchPhotoUploadUrls(
    formUid: string,
    files: BatchPresignUploadRequest[],
  ): Promise<BatchPresignUploadResponse> {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms/${formUid}/media/batch/presign`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ files }),
      },
    );

    return handleApiResponse(response);
  },

  async registerMediaBatch(
    formUid: string,
    items: BatchMediaRegistrationRequestItem[],
  ): Promise<BatchMediaRegistrationResponse> {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms/${formUid}/media/batch`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      },
    );

    return handleApiResponse(response);
  },

  async uploadPhoto(
    formUid: string,
    file: File,
    options?: {
      photoPosition?: string;
      photoSequence?: number;
      replaceMediaId?: string;
    },
  ): Promise<MediaRegistrationResponse> {
    const presignResponse = await this.getPhotoUploadUrl(
      formUid,
      file.type,
      file.size,
      file.name,
    );
    await this.uploadPhotoToCloud(presignResponse.data.uploadUrl, file);
    return this.registerPhoto(
      formUid,
      presignResponse.data.bucketName,
      presignResponse.data.storagePath,
      file.name,
      file.type,
      file.size,
      options?.photoPosition,
      options?.photoSequence,
      options?.replaceMediaId,
    );
  },

  async sendVerificationCode(email: string, formUid: string) {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/send-verification-code`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, method: "otp_email", formUid }),
      },
    );

    return handleApiResponse(response) as Promise<{ data: OtpChallenge }>;
  },

  async verifyEmail(
    email: string,
    token: string,
    formUid: string,
    challengeId?: string,
  ) {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/verify-email`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, token, challengeId, formUid }),
      },
    );

    return handleApiResponse(response) as Promise<VerificationResponse>;
  },

  async startAuth(payload: {
    email: string;
    formUid: string;
    referenceUid: string;
    forceResend?: boolean;
  }) {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/auth/start`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );

    return handleApiResponse(response) as Promise<{ data: MagicLinkChallenge }>;
  },

  async completeMagicLinkCallback(body: {
    accessToken: string;
    refreshToken?: string;
    formUid: string;
    referenceUid: string;
  }) {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/auth/magic-link/callback`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
    );

    return handleApiResponse(response);
  },

  async getIntakeStatus(formUid: string) {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms/${formUid}/status?includeMedia=true`,
    );

    return handleApiResponse(response);
  },

  async getIntakeFormByEmail(email: string) {
    // Validate email before making request
    if (!email || typeof email !== 'string' || email.trim().length === 0) {
      throw new Error("Email is required");
    }

    const trimmedEmail = email.trim().toLowerCase();
    const encodedEmail = encodeURIComponent(trimmedEmail);
    
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms/by-email/${encodedEmail}`,
    );

    // Handle 404 specifically (form not found)
    if (response.status === 404) {
      throw new Error(`No intake form found for email: ${trimmedEmail}`);
    }

    // Handle 422 specifically (validation error)
    if (response.status === 422) {
      try {
        const errorData = await response.json();
        const errorMessage = errorData?.error?.message || errorData?.message || "Invalid email or intake form not found";
        throw new Error(errorMessage);
      } catch (parseError) {
        throw new Error(`Invalid email format or intake form not found for: ${trimmedEmail}`);
      }
    }

    return handleApiResponse(response);
  },

  async updateIntake(formUid: string, data: IntakeData) {
    const payload = buildFormPayload(data);
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms/${formUid}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ formData: payload }),
      },
    );
    return handleApiResponse(response);
  },

  async getFormMedia(formUid: string): Promise<unknown[]> {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms/${formUid}/media`,
    );

    const payload = (await handleApiResponse(response)) as unknown;

    if (Array.isArray(payload)) {
      return payload;
    }

    if (payload && typeof payload === "object") {
      const { data } = payload as { data?: unknown };
      if (Array.isArray(data)) {
        return data;
      }
    }

    return [];
  },

  async deleteForm(formUid: string) {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/forms/${formUid}`,
      {
        method: "DELETE",
      },
    );

    return handleApiResponse(response);
  },

  async getSpecialties(): Promise<{ data: Specialty[] }> {
    const response = await fetch(
      `${getBackendUrl()}/api/v1/patient-intake/specialties`,  
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      },
    );

    return handleApiResponse(response);
  },
};
